/**
 * Utilities: Scroll
 *
 * @module utility/UScroll
 * @example import { UScroll } from '../common/utility/u-scroll';
 */


// Dependencies
// ============================================================================

import { $ } from '../utility/u-jquery-$';


// ƒ
// ============================================================================

/**
 * Check if an element is in view. Usually wrapped in a scroll event listener
 *
 * @example $( window ).on( 'scroll', () => { this.checkInView(); });
 *
 * @param   {jQuery}  el           Element. Can be either a jQuery object or HTML element
 * @param   {bool}    fullyInView  If true, the element should be showing completely. If false, only part of it needs to be showing
 *
 * @return  {void}
 */
function isElementInView( el, fullyInView = true )
{
	const $el        = ( el instanceof jQuery ) ? el : $( el );
	const pageTop    = $(window).scrollTop();
	const pageBottom = pageTop + $(window).height();
	const elTop      = $el.offset().top;
	const elBottom   = elTop + $el.height();

	if ( fullyInView )
	{
		return ( ( pageTop < elTop ) && ( pageBottom > elBottom ) );
	}
	else
	{
		return ( (elTop <= pageBottom ) && ( elBottom >= pageTop ) );
	}
}

/**
 * Legacy version of the above func, from the old codebase.
 * Changes the thresholds, but means this may not trigger when expected
 *
 * @param   {HTMLElement}  elem  Element
 *
 * @return  {bool}         True if in view, otherwise false
 */
function isScrolledIntoViewLegacy(elem) {
	var viewportTop = $(window).scrollTop();
	var viewportHeight = $(window).height();

	var elemTop = $(elem).offset().top;

	return (elemTop >= (viewportTop + (viewportHeight / 6.66)) && elemTop <= (viewportTop + (viewportHeight / 3.33)));
}



function isElementInViewIO( targetEl, inViewCallback, userOptions = {} )
{
	intersectionObserverInViewChecker( targetEl, ( entries, observer ) =>
	{
		// If intersectionRatio is 0, the target is out of view
		// and we do not need to do anything
		if ( entries[0].intersectionRatio <= 0 )
		{
			return;
		}

		//@debug
		console.log( '[isElementInViewIO] Element is in view:', { target: entries[0].target } );

		if ( inViewCallback && typeof inViewCallback === 'function' )
		{
			inViewCallback();
		}
		else
		{
			console.log( '[isElementInViewIO] No callback provided' );
		}

	}, userOptions );
}

/**
 * Check if an element is in view, using IntersectionObserver.
 * A more performant way to do the same thing as `isElementInView`, that doesn't
 * require wrapping it in a scroll event listener
 *
 * Note that targetEl should be an HTML element, not a jQuery object
 *
 * @link https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API
 * @link https://developer.mozilla.org/en-US/docs/Web/API/IntersectionObserver
 *
 * @return  {void}
 */
function intersectionObserverInViewChecker( targetEl, callback, userOptions = {} )
{
	targetEl = ( targetEl instanceof jQuery ) ? targetEl[0] : targetEl;

	// Set up options
	// ------------------------------------------------------------------------

	const defaultOpts = {
		// Visible amount of item shown in relation to root (0 = 0% = any amount of the target is in view)
		// If this was 10% ie. 0.1, then 10% of the element would have to be in view
		thresholdPerc: 0.0,
	};

	// Merge user options and defaults
	const options = Object.assign( defaultOpts, userOptions );

	const ioOptions = {
		root:       null,  // Relative to document viewport
		rootMargin: '0px', // Margin around root. Values are similar to css property. Unitless values not allowed
		threshold:  options.thresholdPerc,
	}

	// Create observer
	// ------------------------------------------------------------------------

	const observer = new IntersectionObserver( ( entries, observer ) => {
		callback( entries, observer )
	}, ioOptions );

	// Start observing!
	// ------------------------------------------------------------------------

	observer.observe( targetEl );
}


/**
 * Object containing data on an element's position
 *
 * @typedef   {Object}  ElementPositionObject
 *
 * @property  {number}  windowHeight    Height of the window
 * @property  {number}  docViewTop      Distance the user has scrolled down (window.scrollTop). This is 0 if the user hasn't scrolled yet
 * @property  {number}  docViewBottom   Distance the user has scrolled down, plus the window height. Could be used for sticky footer things
 * @property  {number}  elTop           Top position of element
 * @property  {number}  elBottom        Bottom position of element
 * @property  {number}  distanceTop     Distance between the element and document's top
 * @property  {number}  distanceBottom  Distance between the element and document's bottom
 * @property  {number}  percentTop      % value of `distanceTop`
 * @property  {number}  percentBottom   % value of `distanceBottom`
 * @property  {bool}    isInView        True if the element is currently shown in the viewport, false otherwise
 */

/**
 * Returns an object containing positions.
 * Use (result).isInView to check if in view
 *
 * Adapted from: https://stackoverflow.com/a/488073
 *
 * @param   {HTMLElement}  el  Element to check
 *
 * @return  {ElementPositionObject}  Object with multiple properties
 */
function getElementPositions( el )
{
	const windowHeight  = $( window ).height();

	const docViewTop    = $( window ).scrollTop();
	const docViewBottom = docViewTop + $( window ).height();

	const elTop         = $( el ).offset().top;
	const elBottom      = elTop + $( el ).height();

	const distanceTop    = elTop - docViewTop;
	const distanceBottom = docViewBottom - elBottom;

	const percentTop     = distanceTop / windowHeight;
	const percentBottom  = distanceBottom / windowHeight;

	const isInView      = ( ( elBottom <= docViewBottom ) && ( elTop >= docViewTop ) );

	const output = {
		windowHeight,
		docViewTop,
		docViewBottom,
		elTop,
		elBottom,
		distanceTop,
		distanceBottom,
		percentTop,
		percentBottom,
		isInView,
	};

	return output;
}


// Exports
// ============================================================================

export const UScroll = { isElementInView, isScrolledIntoViewLegacy, getElementPositions };
